import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import { Warning } from '@mui/icons-material';

const VehicleConflictDialog = ({ 
  open, 
  onClose, 
  conflicts, 
  targetVehicle, 
  onReassign, 
  loading = false 
}) => {
  if (!conflicts || conflicts.length === 0) {
    return null;
  }

  const handleReassign = () => {
    onReassign(conflicts);
  };

  const getFieldDisplayName = (type) => {
    switch (type) {
      case 'license_plate': return 'License Plate';
      case 'vin': return 'VIN';
      case 'unit_number': return 'Unit Number';
      default: return type;
    }
  };

  const formatVehicleDisplay = (vehicle) => {
    const parts = [];
    if (vehicle.make) parts.push(vehicle.make);
    if (vehicle.model) parts.push(vehicle.model);
    if (vehicle.year) parts.push(vehicle.year);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'warning.light',
        color: 'warning.contrastText'
      }}>
        <Warning />
        Duplicate Data Detected
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          The data you're trying to add is already assigned to another vehicle. 
          Would you like to reassign it to this vehicle?
        </Alert>

        <Typography variant="h6" gutterBottom>
          Target Vehicle: <strong>{targetVehicle}</strong>
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Conflicting Data:
          </Typography>
          
          <List dense>
            {conflicts.map((conflict, index) => (
              <ListItem 
                key={index}
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 1,
                  bgcolor: 'grey.50'
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={getFieldDisplayName(conflict.type)} 
                        size="small" 
                        color="warning"
                      />
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {conflict.value}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Currently assigned to: <strong>Unit {conflict.conflictingVehicle.number}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatVehicleDisplay(conflict.conflictingVehicle)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>What happens if you continue:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • The conflicting data will be <strong>removed</strong> from the current vehicle(s)
            <br />
            • The data will be <strong>assigned</strong> to Unit {targetVehicle}
            <br />
            • This action <strong>cannot be undone</strong>
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleReassign}
          disabled={loading}
          variant="contained"
          color="warning"
        >
          {loading ? 'Reassigning...' : 'Yes, Reassign Data'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleConflictDialog;
